import { ReactElement } from "react";
import {
	DateString,
	DetailsBox,
	DetailsBoxVariant,
	DropOver,
	FormattedText,
	Space
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { isNotNil, isNotNilOrWhiteSpace } from "client/Misc/utils";
import { ColoredValueModel } from "client/Api/Workspace/LiveInformation/Integration/integrationLiveInformation";
import { ResultMapperColor } from "client/Api/IntegrationBuilder/integrationBuilderFlows";
import { TimeZone } from "client/DateAndTime/timezones";
import { ColoredText } from "../ColoredText";
import { PlanningItemModel } from "./PlanningLiveTileContent";

export type PlanningItemDetailsProps = {
	item: PlanningItemModel;
	appName: string;
	target: HTMLElement;
	onClose: () => void;
	timezone: TimeZone;
};

export const PlanningItemDetails = (props: PlanningItemDetailsProps): ReactElement => {
	const {
		item,
		appName,
		target,
		onClose,
		timezone
	} = props;

	const {
		title,
		startDateTime,
		endDateTime,
		link,
		subtitle1,
		subtitle2,
		subtitle3,
		description,
		detail1,
		detail2,
		detail3,
		detail4,
		detail5
	} = item;

	const titleValue: string | undefined = title?.value;

	const computedTitle: string = isNotNilOrWhiteSpace(titleValue)
		? titleValue
		: localize("_NoTitle");

	return (
		<DropOver
			target={target}
			onClose={onClose}
		>
			<DetailsBox
				variant={
					isNotNil(title?.color)
						? colorToVariantMap[title.color]
						: undefined
				}
				title={computedTitle}
				onClose={onClose}
			>
				<DetailsBox.List>
					<DetailsBox.Item>
						<ColoredText
							trackingId="start-date-time"
							color={startDateTime.color}
						>
							<DateString
								value={startDateTime.value}
								format="DMY"
								timezone={timezone}
							/>
							<Space />
							<DateString
								value={startDateTime.value}
								format="T"
								timezone={timezone}
							/>
						</ColoredText>
						<Space />-<Space />
						<ColoredText
							trackingId="end-date-time"
							color={endDateTime.color}
						>
							<DateString
								value={endDateTime.value}
								format="DMY"
								timezone={timezone}
							/>
							<Space />
							<DateString
								value={endDateTime.value}
								format="T"
								timezone={timezone}
							/>
						</ColoredText>
					</DetailsBox.Item>
					<DetailsItem
						value={subtitle1}
						trackingId="subtitle-1"
					/>
					<DetailsItem
						value={subtitle2}
						trackingId="subtitle-2"
					/>
					<DetailsItem
						value={subtitle3}
						trackingId="subtitle-3"
					/>
					<DetailsItem
						value={detail1}
						trackingId="detail-1"
					/>
					<DetailsItem
						value={detail2}
						trackingId="detail-2"
					/>
					<DetailsItem
						value={detail3}
						trackingId="detail-3"
					/>
					<DetailsItem
						value={detail4}
						trackingId="detail-4"
					/>
					<DetailsItem
						value={detail5}
						trackingId="detail-5"
					/>
					{isNotNilOrWhiteSpace(description?.value) && (
						<>
							<DetailsBox.Divider />
							<DetailsItem
								value={description}
								trackingId="description"
							/>
						</>
					)}
					{isNotNilOrWhiteSpace(link) && (
						<DetailsBox.Anchor
							href={link}
							target="_blank"
						>
							<FormattedText
								text={localize("_OpenInAppName")}
								thingsToReplace={[
									{ what: "{0}", filler: appName }
								]}
							/>
						</DetailsBox.Anchor>
					)}
				</DetailsBox.List>
			</DetailsBox>
		</DropOver>
	);
};
PlanningItemDetails.displayName = "PlanningItemDetails";

type DetailsItemProps = {
	value: ColoredValueModel<string> | null;
	trackingId: string;
};

const DetailsItem = (props: DetailsItemProps): ReactElement | null => {
	const {
		value,
		trackingId
	} = props;

	if (!isNotNilOrWhiteSpace(value?.value)) {
		return null;
	}

	return (
		<DetailsBox.Item>
			<ColoredText
				trackingId={trackingId}
				color={value.color}
			>
				{value.value}
			</ColoredText>
		</DetailsBox.Item>
	);
};
DetailsItem.displayName = "DetailsItem";

const colorToVariantMap: Record<ResultMapperColor, DetailsBoxVariant> = {
	[ResultMapperColor.Green]: "success",
	[ResultMapperColor.Blue]: "info",
	[ResultMapperColor.Orange]: "warning",
	[ResultMapperColor.Red]: "danger",
	[ResultMapperColor.Grey]: "secondary",
	[ResultMapperColor.Default]: "default",
	[ResultMapperColor.Purple]: "calendar-6",
	[ResultMapperColor.Cyan]: "calendar-9"
};
